import React from 'react';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import {
  AutomobileDetailedHistory,
  AutomobileEventData,
} from '@/reports/types/vehicles';
import {
  Accordion,
  Text,
  Stack,
  Card,
  Table,
  Box,
  Link,
  useTheme,
} from '@/theme/index';
import { commaFormat } from '@/utils/index';
import { scrollToElement } from '@/utils/scrollToElement';
import { useScreenSize } from '@/utils/useScreenSize';

interface DetailedHistoryProps {
  /** Created in the vehicle report serializer, this is an array of events that have happened to the vehicle. */
  detailedHistory: AutomobileDetailedHistory[];
  ymm: string;
}

/**
 * This Component is used to display the Detailed History section of the report.
 * Detailed History is a condensed list of events that have happened to the vehicle.
 * including accidents, salvages, thefts, and sales listings.
 */
export function VehicleDetailedHistory({
  detailedHistory,
  ymm,
}: DetailedHistoryProps) {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const mobileTable = (
    data: AutomobileEventData,
    item: AutomobileDetailedHistory
  ) => {
    const dataValues = [
      [data?.heading || '-', data?.value || '-'],
      ['Odometer', item?.odometer || '-'],
      ['Location', item?.location || '-'],
    ];

    return <Table className="striped flipped" rows={dataValues} />;
  };

  const headerRowStyle = () => ({
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    fontWeight: 'bold',
    textAlign: 'left',
  });

  const sortedDetailedHistory = [...detailedHistory].sort((a, b) => {
    if (a.parsedDate && b.parsedDate) {
      return (
        new Date(a.parsedDate || 0).getTime() -
        new Date(b.parsedDate || 0).getTime()
      );
    }
    if (!a.parsedDate) {
      return 1;
    }
    return -1;
  });

  const mobileList = (item: AutomobileDetailedHistory) => {
    const date = item.parsedDate;
    return (
      <Accordion
        children={
          <Stack alignItems="flex-start">
            {item?.event?.data?.length &&
              item?.event?.data.map((data: AutomobileEventData) => {
                return mobileTable(data, item);
              })}
            <Link
              onClick={() => scrollToElement(item.event?.id)}
              color="success.main"
            >
              See Details
            </Link>
          </Stack>
        }
        display={
          <Stack>
            <Box>
              <Text>{date || '-'}</Text>
            </Box>
            <Box>
              <Text sx={{ color: 'black', fontWeight: 'bold' }}>
                {item?.event?.type?.visible || '-'}
              </Text>
            </Box>
          </Stack>
        }
      />
    );
  };

  const buildRows = () => {
    if (sortedDetailedHistory.length <= 0) return [];
    return sortedDetailedHistory.map((item: AutomobileDetailedHistory) => {
      return [
        item.parsedDate || '',
        <Box>
          <Text variant="h5" fontWeight="bold">
            {item?.event?.type?.visible}
          </Text>
          {item?.event?.data?.length && item?.event?.data[0]?.heading && (
            <Text mb={0.5}>
              {item?.event?.data[0]?.heading}:{' '}
              {item?.event?.data[0]?.value || '-'}
            </Text>
          )}
          <Link
            onClick={() => scrollToElement(item.event?.id)}
            color="success.main"
          >
            See Details
          </Link>
        </Box>,
        item.odometer ? commaFormat(parseInt(item.odometer)) : '-',
        item.location ? item.location : '-',
      ];
    });
  };

  const displayTables = () => {
    if (isMobile) {
      return sortedDetailedHistory.map((item: AutomobileDetailedHistory) => {
        return mobileList(item);
      });
    }
    return (
      <Table
        className="striped"
        headerRowStyle={headerRowStyle()}
        headers={['Date', 'Event', 'Odometer', 'Location']}
        rows={buildRows()}
      />
    );
  };

  return (
    <ReportSection
      showTopBar
      title="Possible Detailed History"
      hasContent={sortedDetailedHistory.length > 0}
      sectionId="detailed-history"
      personName={ymm}
    >
      <Card
        data-testid="detailed-history"
        variant="outlined"
        sx={(theme) => ({ padding: theme.spacing(2) })}
      >
        <Text variant="h3">Detailed history for this vehicle</Text>
        <Text marginY={2}>
          Below are all major events we have obtained for this vehicle,
          including accidents, salvages, thefts, and sales listings.
        </Text>
        {displayTables()}
      </Card>
    </ReportSection>
  );
}
